import { useNavigate } from "react-router-dom";
import { ChevronLeft, Mail, Phone, Instagram, Linkedin } from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";

interface ContactChannel {
  label: string;
  value: string;
  href: string;
  icon: React.ReactNode;
  isPlaceholder?: boolean;
}

// lucide-react has no TikTok glyph, so it's inlined here to match the other icons.
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M16.6 5.82c-.9-.95-1.4-2.21-1.4-3.52h-3.02v14.02c0 1.54-1.25 2.79-2.79 2.79a2.79 2.79 0 0 1 0-5.58c.28 0 .55.04.8.12V10.5a5.97 5.97 0 0 0-.8-.05A5.83 5.83 0 0 0 3.56 16.3 5.83 5.83 0 0 0 9.39 22.1a5.83 5.83 0 0 0 5.83-5.82V8.77a8.62 8.62 0 0 0 5.03 1.61V7.35a5.65 5.65 0 0 1-3.65-1.53z" />
  </svg>
);

// lucide-react has no WhatsApp glyph either.
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.39 1.26 4.82L2 22l5.44-1.36a9.87 9.87 0 0 0 4.6 1.15h.01c5.46 0 9.9-4.45 9.9-9.91C21.95 6.45 17.5 2 12.04 2Zm0 18.02a8.1 8.1 0 0 1-4.13-1.14l-.3-.18-3.09.78.82-3.02-.2-.31a8.07 8.07 0 0 1-1.24-4.24c0-4.47 3.64-8.11 8.13-8.11 4.48 0 8.12 3.64 8.12 8.11 0 4.48-3.64 8.11-8.11 8.11Zm4.44-6.08c-.24-.12-1.43-.71-1.65-.79-.22-.08-.38-.12-.54.12-.16.24-.62.79-.76.95-.14.16-.28.18-.52.06-.24-.12-1.01-.37-1.92-1.18-.71-.63-1.19-1.41-1.33-1.65-.14-.24-.01-.37.11-.49.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.54-1.3-.74-1.78-.19-.46-.39-.4-.54-.4-.14-.01-.3-.01-.46-.01-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2s.86 2.32.98 2.48c.12.16 1.7 2.6 4.12 3.64.58.25 1.03.4 1.38.51.58.18 1.11.16 1.53.1.47-.07 1.43-.58 1.63-1.15.2-.56.2-1.04.14-1.15-.06-.11-.22-.17-.46-.29Z" />
  </svg>
);

const directChannels: ContactChannel[] = [
  {
    label: "Email",
    value: "support@fleetrify.com",
    href: "mailto:support@fleetrify.com",
    icon: <Mail className="w-5 h-5" />,
  },
  {
    label: "Phone",
    value: "+2349115459143",
    href: "tel:+2349115459143",
    icon: <Phone className="w-5 h-5" />,
  },
];

const WHATSAPP_NUMBER = "2349115459143";

const socialChannels: ContactChannel[] = [
  {
    label: "Instagram",
    value: "fleetrify_technologies",
    href: "https://www.instagram.com/fleetrify_technologies?stkn=MTFqcWh0azVnYXRzNg%3D%3D&utm_source=qr",
    icon: <Instagram className="w-5 h-5" />,
  },
  {
    label: "LinkedIn",
    value: "Fleetrify Technologies",
    href: "https://www.linkedin.com/company/fleetrifytechnologies",
    icon: <Linkedin className="w-5 h-5" />,
  },
  {
    label: "TikTok",
    value: "Fleetrify on TikTok",
    href: "https://vt.tiktok.com/ZSqfg1Gvu/",
    icon: <TikTokIcon className="w-5 h-5" />,
  },
];

const ChannelCard = ({ channel }: { channel: ContactChannel }) => {
  const content = (
    <div className="flex items-center gap-4 bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:border-fleetblue/30 hover:shadow-md transition-all h-full">
      <div className="w-11 h-11 shrink-0 rounded-xl bg-fleetblue/10 text-fleetblue flex items-center justify-center">
        {channel.icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
          {channel.label}
        </p>
        <p
          className={`text-sm font-semibold mt-0.5 truncate ${
            channel.isPlaceholder ? "text-gray-400 italic" : "text-fleetdark"
          }`}
        >
          {channel.value}
        </p>
      </div>
    </div>
  );

  if (channel.isPlaceholder) {
    return content;
  }

  return (
    <a
      href={channel.href}
      target={channel.href.startsWith("http") ? "_blank" : undefined}
      rel="noreferrer"
      className="block"
    >
      {content}
    </a>
  );
};

const ContactSupport = () => {
  const navigate = useNavigate();

  return (
    <DashboardLayout
      title="Contact Support"
      description="Reach the Fleetrify support team through any of the channels below."
      containerClass="pb-10"
    >
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm font-medium text-fleetblue hover:text-blue-800 transition-colors mt-6 mb-6"
      >
        <ChevronLeft className="w-4 h-4" />
        Back
      </button>

      <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-gray-100 mb-6">
        <h2 className="text-lg font-bold text-fleetdark">We're here to help</h2>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Have a question, need assistance with your account, or want to give us feedback?
          Reach out to the Fleetrify support team through any channel below and we'll get
          back to you as soon as possible.
        </p>
      </div>

      <div className="mb-8">
        <h3 className="text-sm font-bold text-fleetdark mb-3">Direct contact</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {directChannels.map((channel) => (
            <ChannelCard key={channel.label} channel={channel} />
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold text-fleetdark mb-3">Follow &amp; message us</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {socialChannels.map((channel) => (
            <ChannelCard key={channel.label} channel={channel} />
          ))}
        </div>
      </div>

      {/* Floating WhatsApp button — bounce lives on the inner circle and the
          hover scale on the outer link, so the two transforms don't fight
          each other and cancel the bounce while hovering. */}
      <a
        href={`https://wa.me/${WHATSAPP_NUMBER}`}
        target="_blank"
        rel="noreferrer"
        aria-label="Chat with Fleetrify on WhatsApp"
        title="Chat with us on WhatsApp"
        className="fixed bottom-6 right-6 z-50 hover:scale-105 transition-transform"
      >
        <div className="w-14 h-14 rounded-full bg-[#25D366] text-white shadow-lg flex items-center justify-center animate-bounce">
          <WhatsAppIcon className="w-8 h-8" />
        </div>
      </a>
    </DashboardLayout>
  );
};

export default ContactSupport;
